<script setup lang="ts">
import { email, emailError, validateEmail } from './emailValidation'
import { mockupFormDataEmail } from './data'
import type { IConsentsSection, IInputField, ILegalDescription } from '@/components/PlusBlog/type'
import { transformObjectToSend, useForm } from '@/components/LeadForm/LeadFormMethods'
import { iconChecked } from '@/utils/backgroundIcons'
import accordionIcon from '@/assets/icons/blog/accordion-more.svg'

const props = defineProps<{
    tabIndex: 0 | -1
    actionTitle?: string | undefined
    legalTitle: string
    legalDescription: ILegalDescription
    consentsSection: IConsentsSection
    inputs: IInputField[]
    description: string
}>()

const emit = defineEmits<{
    (e: 'typeMessage', payload: string): void
}>()

const { actionTitle, legalTitle, legalDescription, consentsSection, inputs, description } = toRefs(props)

onMounted(() => {
    setTabIndex()
})

const inputsData: IInputField = inputs.value[0]
const consentsData = consentsSection?.value?.consents[0]

const { isSuccess, isError, sendForms } = useForm()
const consentRefs = reactive<{ [key: string]: boolean }>({})

const consentsItem = consentsData.consents?.map((item, idx) => {
    return {
        ...item,
        name: `ZGODA${idx + 1}`,
        id: `${idx + 1}`,
    }
})

consentsItem?.forEach((item) => {
    consentRefs[item.name] = false
})

const errrorMasageIsAllConstens = ref(false)
const selectedAllConsents = computed({
    get() {
        return Object.values(consentRefs).every(Boolean)
    },
    set(newValue: boolean) {
        for (const key of Object.keys(consentRefs)) {
            consentRefs[key] = newValue
        }
        errrorMasageIsAllConstens.value = !newValue
    },
})

function updateConsent(name: string, event: Event) {
    const target = event.target as HTMLInputElement | null

    if (target && event.type === 'change') {
        const isChecked = target.checked
        consentRefs[name] = isChecked
    } else {
        consentRefs[name] = !consentRefs[name]
    }
    errrorMasageIsAllConstens.value = !selectedAllConsents.value
}

/*
 * DropdownConsents
 */
const refConsents = ref()

const isConsentsDropdownOpen = ref(false)
function toggleConsentsDropdown() {
    isConsentsDropdownOpen.value = !isConsentsDropdownOpen.value
    setConsentsLinksTabIndex()
}

function setConsentsLinksTabIndex() {
    const focusElements = refConsents.value.querySelectorAll('[tabindex]')
    for (const el of focusElements) {
        el.setAttribute('tabindex', isConsentsDropdownOpen.value && props.tabIndex === 0 ? '0' : '-1')
    }
}

/*
 * DropdownRodo
 */
const refRodo = ref<HTMLElement | null>(null)

const isRodoDropdownOpen = ref(false)
function toggleRodoDropdown() {
    isRodoDropdownOpen.value = !isRodoDropdownOpen.value
    setRodoLinksTabIndex()
}

function setRodoLinksTabIndex() {
    const focusElements = refRodo.value?.querySelectorAll('[tabindex]')

    if (focusElements) {
        for (const el of focusElements) {
            el.setAttribute('tabindex', isRodoDropdownOpen.value && props.tabIndex === 0 ? '0' : '-1')
        }
    }
}
/**
 * SetTabIndex
 */
const refNewsletterForm = ref()
function setTabIndex() {
    if (!refNewsletterForm.value) {
        return
    }
    const formElements = refNewsletterForm.value?.querySelectorAll('a, button, input, [tabindex]')
    formElements.forEach((el: Element) => {
        el.setAttribute('tabindex', props.tabIndex === -1 ? '-1' : '0')
    })
    setRodoLinksTabIndex()
    setConsentsLinksTabIndex()
}

/*
 * Valid Forms
 */
const consentsAllError = consentsSection.value.validation['all.consents.required']

function validateForm() {
    validateEmail(inputsData.validation)
    return !emailError.value && selectedAllConsents.value
}

/*
 * Send Form
 */

async function onSubmit() {
    if (!validateForm()) {
        console.error('Formularz zawiera błędy')
        return
    }

    const dataFromTheForm = transformObjectToSend({
        EMAIL: email.value,
        ...consentRefs,
    })

    const sendData = {
        event_type: 'glcf',
        mail_title: 'www_global_lead',
        fields: [...mockupFormDataEmail, ...dataFromTheForm],
    }
    await sendForms(sendData)

    if (isSuccess.value) {
        emit('typeMessage', 'success')
        email.value = ''
    }

    if (isError.value) {
        emit('typeMessage', 'error')
    }
}

function validateConsents() {
    errrorMasageIsAllConstens.value = !selectedAllConsents.value

    if (errrorMasageIsAllConstens.value) {
        isConsentsDropdownOpen.value = true
    }
}

watch(
    consentRefs,
    () => {
        errrorMasageIsAllConstens.value = !selectedAllConsents.value
    },
    { deep: true },
)

watch(
    () => props.tabIndex,
    () => {
        setTabIndex()
    },
)
</script>

<template>
    <form
        id="newsletter-form"
        ref="refNewsletterForm"
        class="page-plus form c-focus w-full"
        novalidate
        @submit.prevent="onSubmit"
    >
        <div class="md:pl-6">
            <p
                class="mb-4 font-light"
                v-html="description"
            />
            <div class="text-pretty md:max-w-[34ch]">
                <div>
                    <label
                        :for="inputsData.label"
                        class="text-sm font-light"
                    >
                        {{ inputsData.label }}
                    </label>
                    <input
                        :id="inputsData.label"
                        v-model="email"
                        :type="inputsData.type"
                        :name="inputsData.label"
                        :aria-required="inputsData.required"
                        placeholder="przyklad@przyklad.pl"
                        class="input-container mt-1 h-12 w-full rounded border-4 border-plus-primary pl-3"
                        :required="inputsData.required"
                        :class="{ 'border-red-500': emailError }"
                        @blur="validateEmail(inputsData.validation)"
                    />
                    <span
                        v-if="emailError"
                        class="text-xs text-red-500"
                    >
                        {{ emailError }}
                    </span>
                </div>
                <!-- Consents -->
                <div class="consents my-6">
                    <div class="flex items-start justify-between">
                        <label class="flex cursor-pointer items-center pr-3">
                            <input
                                v-model="selectedAllConsents"
                                type="checkbox"
                                name="all-consents"
                                class="hidden"
                            />
                            <span
                                class="relative mr-3 size-6 rounded-sm border-2 border-plus-primary bg-white"
                                :class="{
                                    '!bg-plus-primary': selectedAllConsents,
                                    'border-red-500': errrorMasageIsAllConstens,
                                }"
                                tabindex="0"
                                :style="{
                                    backgroundImage: selectedAllConsents ? `url(${iconChecked})` : 'none',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                }"
                                @keydown.enter.prevent="selectedAllConsents = !selectedAllConsents"
                                @keydown.space.prevent="selectedAllConsents = !selectedAllConsents"
                            />
                            <span
                                class="text-paragraph-xs font-light"
                                v-html="consentsData.label"
                            />
                        </label>
                        <button
                            type="button"
                            class="toggle-btn flex items-center gap-2 text-sm font-light"
                            @click="toggleConsentsDropdown"
                        >
                            {{ isConsentsDropdownOpen ? 'Zwiń' : 'Rozwiń' }}

                            <AtomImage
                                :src="accordionIcon"
                                :img-class="[isConsentsDropdownOpen ? 'rotate-180' : 'rotate-0']"
                            />
                        </button>
                    </div>
                    <span
                        v-if="errrorMasageIsAllConstens"
                        class="text-xs text-red-500"
                    >
                        {{ consentsAllError }}
                    </span>

                    <div
                        ref="refConsents"
                        class="accordion"
                        :class="{ 'open-accordion': isConsentsDropdownOpen }"
                    >
                        <div class="ml-[-8px]">
                            <ul class="pl-2 font-normal">
                                <li
                                    v-for="item in consentsItem"
                                    :key="item.id"
                                >
                                    <label
                                        :for="item.id"
                                        class="relative my-3 flex cursor-pointer items-center"
                                    >
                                        <input
                                            :id="item.id"
                                            type="checkbox"
                                            :value="consentRefs[item.name]"
                                            :name="item.name"
                                            class="hidden"
                                            @change="updateConsent(item.name, $event)"
                                            @keydown.enter.prevent="updateConsent(item.name, $event)"
                                            @keydown.space.prevent="updateConsent(item.name, $event)"
                                        />
                                        <span
                                            class="absolute left-0 top-0 mr-3 !size-6 rounded-sm border-2 border-plus-primary bg-white"
                                            :class="{
                                                '!bg-plus-primary': consentRefs[item.name],
                                                'border-red-500': errrorMasageIsAllConstens,
                                            }"
                                            :style="{
                                                backgroundImage: consentRefs[item.name]
                                                    ? `url(${iconChecked})`
                                                    : 'none',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'center',
                                            }"
                                            :tabindex="isConsentsDropdownOpen ? 0 : -1"
                                            @keydown.enter.prevent="updateConsent(item.name, $event)"
                                            @keydown.space.prevent="updateConsent(item.name, $event)"
                                        />
                                        <span class="ml-9 text-sm font-normal">
                                            {{ item.label }}
                                        </span>
                                    </label>
                                </li>
                            </ul>
                            <div
                                class="statement text-sm"
                                v-html="consentsData.legal"
                            />
                        </div>
                    </div>
                </div>
                <!-- RODO -->
                <div class="rodo">
                    <div
                        class="mb-3 flex items-start justify-between"
                        @click="toggleRodoDropdown"
                    >
                        <p
                            class="text-paragraph-xs font-light md:max-w-[34ch]"
                            v-html="legalTitle"
                        />

                        <button
                            type="button"
                            class="toggle-btn flex items-center gap-2 text-sm font-light"
                        >
                            {{ isRodoDropdownOpen ? 'Zwiń' : 'Rozwiń' }}
                            <AtomImage
                                :src="accordionIcon"
                                :img-class="[isRodoDropdownOpen ? 'rotate-180' : 'rotate-0']"
                            />
                        </button>
                    </div>
                    <div
                        ref="refRodo"
                        class="accordion"
                        :class="{ 'open-accordion': isRodoDropdownOpen }"
                    >
                        <div
                            class="content-consent c-scrollbar"
                            :tabindex="isRodoDropdownOpen ? 0 : -1"
                        >
                            <div
                                class="gdpr text-paragraph-xs pb-3"
                                v-html="legalDescription.value"
                            />
                        </div>
                    </div>
                </div>

                <button
                    id="newsletter-form-submit"
                    type="submit"
                    class="my-8 h-12 w-full bg-plus-primary font-bold text-plus-secondary"
                    @click="validateConsents"
                >
                    {{ actionTitle }}
                </button>
            </div>
        </div>
    </form>
</template>

<style lang="scss" scoped>
.accordion {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 500ms;
}
.accordion.open-accordion {
    grid-template-rows: 1fr;
    .content-consent {
        max-height: 336px;
        overflow-y: auto !important;
        padding-right: 4px;
    }
}
.accordion > div {
    overflow: hidden;
}
.input-container {
    border-radius: 4px;
    &::placeholder {
        color: #b3bfc5;
        font-weight: 400;
    }
}

#newsletter-form-submit {
    border-radius: 1.5rem;
}

.gdpr:deep(h3) {
    font-weight: 700;
    margin: 1rem 0;
}
.gdpr:deep(a) {
    font-size: 1rem;
}
.statement:deep(p) {
    margin-bottom: 24px;
}
</style>
